<template>
  <header class="flex h-14 shrink-0 items-center justify-between border-b border-line bg-surface px-4 sm:px-6">
    <div class="flex min-w-0 items-center gap-2">
      <!-- Mobile: open the off-canvas sidebar drawer. -->
      <button
        type="button"
        class="-ml-1 flex size-9 items-center justify-center rounded-lg text-muted transition-colors hover:bg-gray-100 hover:text-ink lg:hidden"
        :title="t('layout.expandSidebar')"
        @click="toggleMobile"
      >
        <Menu class="size-[18px]" />
      </button>
      <!-- Desktop: collapse the sidebar to an icon rail. -->
      <button
        type="button"
        class="-ml-2 hidden size-9 items-center justify-center rounded-lg text-muted transition-colors hover:bg-gray-100 hover:text-ink lg:flex"
        :title="collapsed ? t('layout.expandSidebar') : t('layout.collapseSidebar')"
        @click="toggleSidebar"
      >
        <PanelLeftOpen v-if="collapsed" class="size-[18px]" />
        <PanelLeftClose v-else class="size-[18px]" />
      </button>
      <nav class="flex min-w-0 items-center gap-1.5 text-sm">
        <template v-if="crumb.section">
          <span class="hidden text-muted sm:inline">{{ crumb.section }}</span>
          <ChevronRight class="hidden size-3.5 text-line-strong sm:block" />
        </template>
        <span class="truncate font-semibold text-ink">{{ crumb.title }}</span>
      </nav>
    </div>

    <div class="flex items-center gap-2">
      <!-- Assistant dock toggle -->
      <button
        type="button"
        class="flex h-9 items-center gap-1.5 rounded-full px-3 text-sm font-medium transition-colors"
        :class="dockOpen ? 'bg-brand-50 text-brand-700' : 'text-muted hover:bg-gray-100 hover:text-ink'"
        :title="t('layout.askAssistant')"
        @click="toggleDock"
      >
        <Sparkles class="size-[18px]" />
        <span class="hidden sm:inline">{{ t('layout.assistant') }}</span>
      </button>

      <!-- Language switcher -->
      <button
        type="button"
        class="flex h-9 items-center gap-1 rounded-lg px-2 text-xs font-semibold uppercase text-muted transition-colors hover:bg-gray-100 hover:text-ink"
        :title="t('common.language')"
        @click="toggleLocale"
      >
        <Languages class="size-[18px]" />
        {{ locale }}
      </button>

      <!-- Theme toggle -->
      <button
        type="button"
        class="flex size-9 items-center justify-center rounded-lg text-muted transition-colors hover:bg-gray-100 hover:text-ink"
        :title="theme === 'dark' ? t('layout.switchToLight') : t('layout.switchToDark')"
        @click="toggle"
      >
        <Sun v-if="theme === 'dark'" class="size-[18px]" />
        <Moon v-else class="size-[18px]" />
      </button>

      <div class="h-5 w-px bg-line"></div>

      <!-- ─── OIDC user menu ─────────────────────────────────────── -->
      <div v-if="auth.mode !== 'permissive'" class="flex items-center gap-2.5 text-sm">
        <!-- Workspace switcher — a dropdown when the user belongs to more
             than one tenant; otherwise a plain label (current behaviour). -->
        <div v-if="auth.tenants.length > 1" class="relative">
          <button
            type="button"
            class="flex items-center gap-1.5 rounded-lg border border-line px-2.5 py-1.5 text-right leading-tight transition-colors hover:bg-gray-100"
            :title="t('layout.switchWorkspace')"
            @click="wsOpen = !wsOpen"
          >
            <Building2 class="size-4 shrink-0 text-faint" />
            <span class="max-w-[12rem] truncate font-medium text-ink">{{ activeWorkspaceLabel }}</span>
            <ChevronsUpDown class="size-3.5 shrink-0 text-faint" />
          </button>
          <template v-if="wsOpen">
            <div class="fixed inset-0 z-30" @click="wsOpen = false"></div>
            <div
              class="absolute right-0 z-40 mt-1.5 w-64 overflow-hidden rounded-xl border border-line bg-surface shadow-lg"
            >
              <p class="border-b border-line bg-surface-2 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted">
                {{ t('layout.workspaces') }}
              </p>
              <ul class="max-h-80 overflow-y-auto py-1">
                <li v-for="ws in auth.tenants" :key="ws.tenant_id">
                  <button
                    type="button"
                    class="flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors hover:bg-gray-100"
                    @click="selectWorkspace(ws.tenant_id)"
                  >
                    <Check
                      class="size-4 shrink-0"
                      :class="ws.is_active ? 'text-brand-600' : 'text-transparent'"
                    />
                    <span class="min-w-0 flex-1">
                      <span class="block truncate text-sm font-medium text-ink">
                        {{ ws.name || ws.tenant_id }}
                      </span>
                      <span class="block text-xs text-faint">{{ ws.role }}</span>
                    </span>
                  </button>
                </li>
              </ul>
            </div>
          </template>
        </div>
        <div v-else class="hidden text-right leading-tight sm:block">
          <p class="font-medium text-ink">{{ auth.userDisplayName ?? t('layout.signedIn') }}</p>
          <p class="text-xs text-faint">
            <template v-if="auth.tenantName">{{ auth.tenantName }}</template>
            <code v-else class="font-mono">{{ auth.tenantId ?? '—' }}</code>
          </p>
        </div>
        <div class="flex size-8 items-center justify-center rounded-full bg-brand-600 text-xs font-bold text-white">
          {{ initials }}
        </div>
        <button
          type="button"
          class="flex size-9 items-center justify-center rounded-lg text-muted transition-colors hover:bg-gray-100 hover:text-ink"
          :title="t('layout.signOut')"
          @click="auth.logout()"
        >
          <LogOut class="size-[18px]" />
        </button>
      </div>

      <!-- ─── Permissive dev tenant + user picker ────────────────── -->
      <div v-else class="flex items-center gap-2 text-sm">
        <span class="inline-flex items-center gap-1.5 rounded-full bg-warning-soft px-2.5 py-1 text-xs font-medium text-warning">
          <span class="size-1.5 rounded-full bg-warning"></span>{{ t('layout.devMode') }}
        </span>
        <label for="tenant" class="text-xs text-faint">{{ t('layout.tenant') }}</label>
        <input
          id="tenant"
          v-model="tenantInput"
          type="text"
          class="w-28 rounded-lg border border-line-strong bg-surface px-2.5 py-1 font-mono text-xs text-ink focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/25"
          @keyup.enter="applyTenant"
          @blur="applyTenant"
        />
        <label for="user" class="text-xs text-faint">{{ t('layout.you') }}</label>
        <input
          id="user"
          v-model="userInput"
          type="text"
          placeholder="spike-anonymous"
          class="w-28 rounded-lg border border-line-strong bg-surface px-2.5 py-1 font-mono text-xs text-ink focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/25"
          :title="t('layout.userSubHint')"
          @keyup.enter="applyUser"
          @blur="applyUser"
        />
        <span v-if="dirty" class="text-xs text-warning" :title="t('layout.applyHint')">{{ t('layout.edited') }}</span>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { Sun, Moon, LogOut, ChevronRight, PanelLeftClose, PanelLeftOpen, Sparkles, Languages, Building2, ChevronsUpDown, Check, Menu } from 'lucide-vue-next';
import { useAuthStore } from '../stores/auth';
import { useTheme } from '../composables/useTheme';
import { useSidebar } from '../composables/useSidebar';
import { useChatDock } from '../composables/useChatDock';
import { setLocale, type AppLocale } from '../i18n';

const route = useRoute();
const auth = useAuthStore();
const { t, locale } = useI18n();
const { theme, toggle } = useTheme();
const { collapsed, toggle: toggleSidebar, toggleMobile } = useSidebar();
const { open: dockOpen, toggle: toggleDock } = useChatDock();

function toggleLocale(): void {
  setLocale((locale.value === 'es' ? 'en' : 'es') as AppLocale);
}

// Friendly breadcrumb per route (section mirrors the sidebar grouping).
// Values are i18n keys resolved in the computed below so the crumb
// re-renders on locale switch.
const CRUMBS: Record<string, { section?: string; title: string }> = {
  home: { title: 'layout.crumbs.home' },
  quickstart: { title: 'layout.crumbs.quickstart' },
  chat: { title: 'layout.crumbs.chat' },
  storyboard: { section: 'layout.sections.design', title: 'layout.crumbs.overview' },
  components: { section: 'layout.sections.design', title: 'layout.crumbs.components' },
  component: { section: 'layout.sections.design', title: 'layout.crumbs.componentEditor' },
  theme: { section: 'layout.sections.design', title: 'layout.crumbs.theme' },
  widgets: { section: 'layout.sections.design', title: 'layout.crumbs.widgetBundles' },
  screens: { section: 'layout.sections.design', title: 'layout.crumbs.screens' },
  design: { section: 'layout.sections.design', title: 'layout.crumbs.designEditor' },
  designer: { section: 'layout.sections.design', title: 'layout.crumbs.dashboardEditor' },
  dashboards: { section: 'layout.sections.design', title: 'layout.crumbs.dashboards' },
  variables: { section: 'layout.sections.data', title: 'layout.crumbs.variables' },
  sources: { section: 'layout.sections.data', title: 'layout.crumbs.sources' },
  agents: { section: 'layout.sections.data', title: 'layout.crumbs.agents' },
  'agent-detail': { section: 'layout.sections.data', title: 'layout.crumbs.agent' },
  alerts: { section: 'layout.sections.monitor', title: 'layout.crumbs.alerts' },
  'llm-config': { section: 'layout.sections.workspace', title: 'layout.crumbs.llmConfig' },
  members: { section: 'layout.sections.workspace', title: 'layout.crumbs.members' },
  organization: { section: 'layout.sections.workspace', title: 'layout.crumbs.organization' },
  settings: { section: 'layout.sections.workspace', title: 'layout.crumbs.settings' },
  audit: { section: 'layout.sections.owner', title: 'layout.crumbs.audit' },
  denials: { section: 'layout.sections.owner', title: 'layout.crumbs.denials' },
  ops: { section: 'layout.sections.opsAdmin', title: 'layout.crumbs.ops' },
};
function prettify(name: string): string {
  return name.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
const crumb = computed(() => {
  const name = String(route.name ?? '');
  if (name === 'project-section') return { section: t('projects.title'), title: t('projectWorkspace.' + String(route.params.section)) };
  if (name === 'project' || name === 'projects') return { title: t('projects.title') };
  const c = CRUMBS[name];
  if (!c) return { title: name ? prettify(name) : 'Lymon' };
  return { section: c.section ? t(c.section) : undefined, title: t(c.title) };
});
const initials = computed(() => {
  const n = auth.userDisplayName ?? '';
  const parts = n.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '·';
  return (parts[0]![0]! + (parts[1]?.[0] ?? '')).toUpperCase();
});

const tenantInput = ref(auth.manualTenantId);
const userInput = ref(auth.manualUserSub);
const dirty = computed(
  () => tenantInput.value !== auth.manualTenantId || userInput.value !== auth.manualUserSub,
);

watch(() => auth.manualTenantId, (next) => { tenantInput.value = next; });
watch(() => auth.manualUserSub, (next) => { userInput.value = next; });

function applyTenant(): void {
  if (tenantInput.value && tenantInput.value !== auth.manualTenantId) {
    auth.setManualTenantId(tenantInput.value);
    window.location.reload();
  }
}
function applyUser(): void {
  if (userInput.value !== auth.manualUserSub) {
    auth.setManualUserSub(userInput.value);
    window.location.reload();
  }
}

// ── Workspace switcher (OIDC) ──────────────────────────────────────────
const wsOpen = ref(false);
const activeWorkspaceLabel = computed(() => {
  const active = auth.tenants.find((w) => w.is_active);
  return active?.name || auth.tenantName || active?.tenant_id || auth.tenantId || '—';
});
async function selectWorkspace(id: string): Promise<void> {
  wsOpen.value = false;
  await auth.switchTenant(id);
}

onMounted(() => {
  if (auth.mode === 'oidc') void auth.loadTenants();
});
</script>
